import { isNotNullOrUndefined } from '@vy/utils';
import React, { useState } from 'react';
import type { InvoiceOverview } from '@vy/api-definition';
import { VyDialog } from '../../../../components/new-design/feedback/VyDialog';
import { VyAutocompleteSelect } from '../../../../components/new-design/inputs/VyAutocompleteSelect';
import { useInvoicesOverviewsNew } from '../../../../hooks/requests/use-invoices-overviews/use-invoices-overviews-new';
import { useAssignPaymentToInvoice } from '../../../../hooks/requests/use-assign-payment-to-invoice';

type AssignPaymentToInvoiceDialogProps = {
  open: boolean;
  onClose: () => void;
  paymentId: string;
  customerId: string;
};

export const AssignPaymentToInvoiceDialog: React.FC<AssignPaymentToInvoiceDialogProps> = ({
  open,
  onClose,
  paymentId,
  customerId,
}) => {
  const getInvoiceOptionLabel = (invoice: InvoiceOverview): string => {
    const invoiceNumber = invoice.invoiceNumber ?? invoice.id;
    const customerName = invoice.customer.name;

    return `${invoiceNumber} — ${customerName}`;
  };

  const filterInvoiceOption = (invoice: InvoiceOverview, searchQuery: string): boolean => {
    const label = getInvoiceOptionLabel(invoice).toLowerCase();

    return label.includes(searchQuery.toLowerCase());
  };

  const getInvoiceOptionKey = (invoice: InvoiceOverview): string => invoice.id;

  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceOverview | null>(null);

  const { data: invoicesData } = useInvoicesOverviewsNew(
    { customerId },
    { enabled: open },
  );

  const { mutateAsync: assignPaymentToInvoice, isLoading } = useAssignPaymentToInvoice();

  const invoices = invoicesData?.invoicesOverviews ?? [];

  const handleOptionSelect = (option: InvoiceOverview | null | string) => {
    if (isNotNullOrUndefined(option) && typeof option !== 'string') {
      setSelectedInvoice(option);
    }
  };

  const handleSubmit = async () => {
    if (isNotNullOrUndefined(selectedInvoice)) {
      await assignPaymentToInvoice({ paymentId, invoiceId: selectedInvoice.id });
      setSelectedInvoice(null);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedInvoice(null);
    onClose();
  };

  return (
    <VyDialog
      open={open}
      onClose={handleClose}
      label="Assign to Invoice"
      cancelButtonText="Cancel"
      ctaText="Assign"
      ctaDisabled={isNotNullOrUndefined(selectedInvoice) === false || isLoading}
      onSubmit={handleSubmit}
      maxWidth="sm"
    >
      <VyAutocompleteSelect<InvoiceOverview>
        label="Invoice"
        options={invoices}
        value={selectedInvoice ?? undefined}
        onOptionSelect={handleOptionSelect}
        getOptionLabel={getInvoiceOptionLabel}
        filterOption={filterInvoiceOption}
        optionsKeyExtractor={getInvoiceOptionKey}
        placeholder="Search invoices..."
        fullWidth
      />
    </VyDialog>
  );
};
